import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

interface CompensationData {
  current_range: { min: number; max: number };
  market_range: { min: number; max: number };
  target_range: { min: number; max: number };
  percentile_current?: number;
  percentile_target?: number;
}

interface CompensationChartProps {
  data: CompensationData;
  title?: string;
}

const CompensationChart: React.FC<CompensationChartProps> = ({
  data,
  title = 'Compensation Analysis & Market Position'
}) => {
  const chartData = [
    {
      category: 'Current',
      min: data.current_range.min,
      max: data.current_range.max,
      mid: (data.current_range.min + data.current_range.max) / 2,
      color: '#0891b2',
      percentile: data.percentile_current
    },
    {
      category: 'Market',
      min: data.market_range.min,
      max: data.market_range.max,
      mid: (data.market_range.min + data.market_range.max) / 2,
      color: '#10b981',
      percentile: 50
    },
    {
      category: 'Target',
      min: data.target_range.min,
      max: data.target_range.max,
      mid: (data.target_range.min + data.target_range.max) / 2,
      color: '#4f7396',
      percentile: data.percentile_target
    }
  ];

  const formatCurrency = (value: number) => {
    return `$${(value / 1000).toFixed(0)}k`;
  };

  const CustomLabel = (props: any) => {
    const { x, y, width, value, index } = props;
    const item = chartData[index];
    return (
      <g>
        <text
          x={x + width / 2}
          y={y - 10}
          fill={item.color}
          textAnchor="middle"
          fontSize={12}
          fontWeight="600"
        >
          {formatCurrency(item.min)}-{formatCurrency(item.max)}
        </text>
        {item.percentile && (
          <text
            x={x + width / 2}
            y={y - 25}
            fill="#6b7280"
            textAnchor="middle"
            fontSize={11}
          >
            {item.percentile}th %ile
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold mb-2 text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 mb-6">
        Compare your current compensation with market rates and target earning potential
      </p>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 50, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
          <XAxis
            dataKey="category"
            tick={{ fill: '#374151', fontSize: 13, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickFormatter={formatCurrency}
            axisLine={false}
            tickLine={false}
            domain={[0, 'auto']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              padding: '12px'
            }}
            formatter={(value: number) => [formatCurrency(value), 'Range']}
            cursor={{ fill: '#f9fafb' }}
          />
          <Bar dataKey="max" radius={[8, 8, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
            <LabelList content={<CustomLabel />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-cyan-900">Current Position</span>
            <div className="w-3 h-3 rounded-full bg-cyan-600"></div>
          </div>
          <p className="text-2xl font-bold text-cyan-900">
            {formatCurrency(data.current_range.min)}-{formatCurrency(data.current_range.max)}
          </p>
          {data.percentile_current && (
            <p className="text-xs text-cyan-700 mt-1">{data.percentile_current}th percentile</p>
          )}
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Market Median</span>
            <div className="w-3 h-3 rounded-full bg-green-600"></div>
          </div>
          <p className="text-2xl font-bold text-green-900">
            {formatCurrency(data.market_range.min)}-{formatCurrency(data.market_range.max)}
          </p>
          <p className="text-xs text-green-700 mt-1">50th percentile</p>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-900">Target Potential</span>
            <div className="w-3 h-3 rounded-full bg-slate-600"></div>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(data.target_range.min)}-{formatCurrency(data.target_range.max)}
          </p>
          {data.percentile_target && (
            <p className="text-xs text-slate-700 mt-1">{data.percentile_target}th percentile</p>
          )}
        </div>
      </div>

      <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p className="text-sm font-medium text-blue-900 mb-1">Growth Opportunity</p>
        <p className="text-xs text-blue-700">
          Potential increase of{' '}
          <span className="font-semibold">
            {formatCurrency(data.target_range.min - data.current_range.max)}
          </span>{' '}
          to{' '}
          <span className="font-semibold">
            {formatCurrency(data.target_range.max - data.current_range.max)}
          </span>{' '}
          with strategic skill development and role positioning
        </p>
      </div>
    </div>
  );
};

export default CompensationChart;
